// src/pages/Map.jsx
import React, { useEffect, useState, useContext, useCallback } from 'react';
import AppContext from '../contexts/AppContext';
import { APIProvider, Map as GoogleMap, Marker, InfoWindow } from '@vis.gl/react-google-maps';
import { useLocation } from 'react-router-dom';
import { FaAngleRight, FaAngleDown } from "react-icons/fa";
import { openDiscover } from '../utils/discover';

const vwToPx = (vw) => {
  return Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) * (vw / 100);
};

const mapIcons = {
  "adventure_activities": `${process.env.PUBLIC_URL}/mapicons/adventure_activities.svg`,
  "bank": `${process.env.PUBLIC_URL}/mapicons/bank.svg`,
  "entertainment": `${process.env.PUBLIC_URL}/mapicons/entertainment.svg`,
  "parks_and_nature": `${process.env.PUBLIC_URL}/mapicons/parks_and_nature.svg`,
  "restaurant": `${process.env.PUBLIC_URL}/mapicons/restaurant.svg`,
  "souvenirs": `${process.env.PUBLIC_URL}/mapicons/souvenirs.svg`,
  "shop": `${process.env.PUBLIC_URL}/mapicons/shop.svg`,
  "transport": `${process.env.PUBLIC_URL}/mapicons/transport.svg`,
  "other": `${process.env.PUBLIC_URL}/mapicons/other.svg`,
  "home": `${process.env.PUBLIC_URL}/mapicons/home.svg`
};

const extractGeoLocations = (attributes) => {
  let geoLocations = [];
  attributes.forEach((attr) => {
    if (attr.geoLocation) {
      geoLocations.push({
        id: attr.id,
        ...attr.geoLocation
      });
    }
    if (attr.attributes && attr.attributes.length > 0) {
      geoLocations = geoLocations.concat(extractGeoLocations(attr.attributes));
    }
  });
  return geoLocations;
};

const createMarkers = (geoLocations) => {
  return geoLocations.filter(location => location.name !== undefined).map((location) => {
    return {
      position: { lat: location.latitude, lng: location.longitude },
      icon: mapIcons[location.type] || mapIcons['other'],
      info: location.description,
      head: location.name,
      category: location.type,
      id: location.id
    };
  });
};


const Map = () => {
  const [mapOptions, setMapOptions] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const { hotelInfo, contentInfo } = useContext(AppContext);
  const [markers, setMarkers] = useState([]);
  const [filteredMarkers, setFilteredMarkers] = useState([]);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(Object.keys(mapIcons));
  const [zoom, setZoom] = useState(9);
  const [randomizedOnce, setRandomizedOnce] = useState(false);
  const location = useLocation();

  const clusterMarkers = useCallback((unfilteredMarkers) => {
    const gridSize = 10 / Math.pow(2, zoom - 1); //这个是避免图标堆叠的 调大数值减少堆叠
    const visibleMarkers = new Set();

    unfilteredMarkers.forEach(marker => {
      const key = `${Math.floor(marker.position.lat / gridSize)},${Math.floor(marker.position.lng / gridSize)}`;
      if (!Array.from(visibleMarkers).some(visibleMarker =>
        `${Math.floor(visibleMarker.position.lat / gridSize)},${Math.floor(visibleMarker.position.lng / gridSize)}` === key
      )) {
        visibleMarkers.add(marker);
      }
    });

    return Array.from(visibleMarkers);
  }, [zoom]);

  useEffect(() => {
    if (contentInfo && !randomizedOnce) {
      // 1. 抽取、打乱业务点
      const geoLocations = extractGeoLocations(contentInfo.attributes);
      for (let i = geoLocations.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [geoLocations[i], geoLocations[j]] = [geoLocations[j], geoLocations[i]];
      }
      const markerObjects = createMarkers(geoLocations);

      // 2. 直接把 home marker 插到最前面
      const homePos = {
        lat: hotelInfo?.landing?.geoLocation?.latitude ?? -37.8136,
        lng: hotelInfo?.landing?.geoLocation?.longitude ?? 144.9631
      };
      const homeMarker = {
        id: 'home',
        position: homePos,
        icon: `${process.env.PUBLIC_URL}/mapicons/home.svg`,
        head: hotelInfo.name || 'Our Venue',         // 可换成酒店名
        info: hotelInfo.landing.description || 'Navigate to our venue',
        category: 'home'
      };
      markerObjects.unshift(homeMarker);

      // 3. 写入 state 并做聚合
      setMarkers(markerObjects);
      setFilteredMarkers(clusterMarkers(markerObjects));
      setRandomizedOnce(true);

      // 4. 如果 URL state 里有要定位的 id，就选择它
      if (location.state?.id) {
        const target = markerObjects.find(m => m.id === location.state.id);
        if (target) setSelectedMarker(target);
      }
    }
  }, [contentInfo, hotelInfo, location.state, clusterMarkers, randomizedOnce]);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/mapconfig.json`)
      .then(response => response.json())
      .then(data => setMapOptions(data))
      .catch(error => console.error('Error loading map config:', error));
  }, []);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const closeButton = document.querySelector('.gm-style-iw button.gm-ui-hover-effect');
          if (closeButton) {
            closeButton.style.width = '20px';
            closeButton.style.height = '20px';
            closeButton.style.marginTop = '12px';
          }

          const spanElement = document.querySelector('.gm-style-iw button.gm-ui-hover-effect span');
          if (spanElement) {
            spanElement.style.width = '20px';
            spanElement.style.height = '20px';
            spanElement.style.margin = '0';
          }
          observer.disconnect();
        }
      });
    });

    if (selectedMarker) {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect();
    };
  }, [selectedMarker]);

  useEffect(() => {
    if (selectedCategories.length === 0) {
      setFilteredMarkers([]);
    } else {
      setFilteredMarkers(clusterMarkers(markers.filter(marker => selectedCategories.includes(marker.category))));
    }
  }, [selectedCategories, markers, clusterMarkers]);

  const handleNavigateToLocation = (lat, lng) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prevCategories =>
      prevCategories.includes(category)
        ? prevCategories.filter(c => c !== category)
        : [...prevCategories, category]
    );
  };

  const handleGlobalCheckbox = () => {
    if (selectedCategories.length === Object.keys(mapIcons).length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(Object.keys(mapIcons));
    }
  }

  const formatLabel = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleZoomChanged = (e) => {
    setZoom(e.detail.zoom);
  };

  const handleInfoWindowClick = () => {
    openDiscover(hotelInfo?.area?.bitlyUrl);
  };

  if (!hotelInfo) { return <div>Loading...</div>; }
  const themeColor = hotelInfo.theme?.primaryColor || hotelInfo.theme?.color || 'rgb(0,0,0)';

  return (
    <div className="absolute z-10 top-0 bottom-16 left-0 overflow-hidden w-full" style={{ height: 'calc{100vh-4rem}' }}>
      {filterPanelOpen && (
        <div
          className="fixed inset-0 bg-gray bg-opacity-50 z-20"
          onClick={() => setFilterPanelOpen(false)}
        />
      )}

      <div className="w-full h-full relative z-10">
        <APIProvider apiKey={'AIzaSyD63grvHYISPK4dLfyj5kKmC6MR_wQPJ80'}>
          {mapOptions && hotelInfo ? (
            <GoogleMap
              defaultCenter={{
                lat: hotelInfo?.landing?.geoLocation?.latitude ?? -37.8136,
                lng: hotelInfo?.landing?.geoLocation?.longitude ?? 144.9631
              }}
              defaultZoom={location.state ? 13 : (hotelInfo?.landing?.geoLocation?.defaultZoom || 9)}
              options={mapOptions}
              className="w-full h-full"
              onZoomChanged={handleZoomChanged}
            >
              {filteredMarkers.map((marker, index) => (
                <Marker
                  key={index}
                  position={marker.position}
                  icon={marker.icon}
                  onClick={() => setSelectedMarker(marker)}
                />
              ))}
              {selectedMarker && (
                <InfoWindow
                  position={selectedMarker.position}
                  onCloseClick={() => setSelectedMarker(null)}
                  headerContent={<span className='font-semibold text-lg px-2' style={{ color: themeColor }}>{selectedMarker.head}</span>}
                  minWidth={vwToPx(90)}
                  maxWidth={vwToPx(100)}
                >
                  <div className='px-2'>
                    <span className=''>{selectedMarker.info}</span>
                    {selectedMarker.category!== 'home' && <p className='text-sm font-semibold py-4' onClick={handleInfoWindowClick}>Click for More Info</p>}
                    <p
                      className='mt-2 text-white text-center py-2 my-2 text-base font-medium rounded-md'
                      onClick={() => handleNavigateToLocation(selectedMarker.position.lat, selectedMarker.position.lng)}
                      style={{ backgroundColor: themeColor }}
                    >
                      Navigate to this Location
                    </p>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          ) : (
            <div>Loading...</div>
          )}
        </APIProvider>
      </div>

      {/* 筛选面板 */}
      {markers.length > 5 &&
        <div
          className={`absolute z-30 mx-2 rounded-md left-0 right-0 bg-white shadow-2xl transition-all duration-300 ease-in-out ${filterPanelOpen ? 'h-atuo' : 'h-12'}`}
          style={{top: '5.5rem'}}
        >
          <button
            className="w-full h-12 flex items-center justify-left pl-6"
            onClick={() => setFilterPanelOpen(!filterPanelOpen)}
          >
            {!filterPanelOpen ? (
              <div className="flex items-center">
                <FaAngleRight className="w-6 h-6" />
                <span className="text-sm pl-2">Filters</span>
              </div>
            ) :
              <div className="flex items-center">
                <FaAngleDown className="w-6 h-6" />
                <span className="text-sm pl-2">Filters</span>
              </div>}
          </button>

          {filterPanelOpen && (
            <div className="px-4 rounded-md overflow-auto max-h-[40vh] overflow-y-auto pl-6">
              <div className="mb-2 flex items-center pl-1">
                <input
                  type="checkbox"
                  id="alltoggle"
                  checked={selectedCategories.length === Object.keys(mapIcons).length}
                  onChange={handleGlobalCheckbox}
                  className="form-checkbox h-4 w-4"
                />
                <span className='pl-4'>All</span>
              </div>
              {Object.entries(mapIcons).map(([key, value], index) => (
                <div key={index} className="flex items-center mb-3 pl-1">
                  <input
                    type="checkbox"
                    id={key}
                    checked={selectedCategories.includes(key)}
                    onChange={() => toggleCategory(key)}
                    className="form-checkbox h-4 w-4"
                  />
                  <img src={value} alt={key} className="h-6 w-6 ml-2 mr-2" />
                  <label htmlFor={key} className="ml-2 text-lg">{formatLabel(key)}</label>
                </div>
              ))}
            </div>
          )}
        </div>
      }
      <div className='h-16 fixed bottom-0 left-0 right-0'>&nbsp;</div>
    </div>
  );

};

export default Map;
